import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Trash2, Sparkles, Bell, Clock, Check, Loader2, Wand2, Activity, Smartphone, AlertCircle, RefreshCw, MapPin, Gauge, Image as ImageIcon, Upload, X } from "lucide-react";

const LANGS = [
  { code: "en", label: "English" },
  { code: "fr", label: "Fran\u00e7ais" },
  { code: "el", label: "\u0395\u03bb\u03bb\u03b7\u03bd\u03b9\u03ba\u03ac" },
  { code: "pl", label: "Polski" },
];

const emptyForm = () => ({
  kind: "custom",
  audience: "all",
  url: "/",
  schedule_at: "",
  image: "",
  title: { en: "", fr: "", el: "", pl: "" },
  body: { en: "", fr: "", el: "", pl: "" },
});

const fmt = (iso) => {
  if (!iso) return "\u2014";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

const kindColor = {
  milestone: "bg-accent/20 text-accent-foreground",
  random: "bg-foreground/10 text-foreground",
  custom: "bg-primary/15 text-primary",
  rsvp: "bg-destructive/10 text-destructive",
  meal: "bg-destructive/10 text-destructive",
};

const AdminPush = () => {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [creating, setCreating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftTopic, setDraftTopic] = useState("");
  const [loading, setLoading] = useState(true);
  const [frequency, setFrequency] = useState(24);
  const [freqBusy, setFreqBusy] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  const onImageUpload = useCallback(async (e) => {
    const file = e.target?.files?.[0];
    e.target.value = ""; // reset so re-picking the same file re-uploads
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Only image files are supported");
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error("Image too large — keep it under 500 KB (target 300 KB for fast delivery)");
      return;
    }
    setImageUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "push-image");
      fd.append("source", "admin");
      // Reuse the existing photo-upload endpoint; it stores in object storage
      // and returns a public URL. Push image field is happy with any HTTPS URL.
      const { data } = await api.post("/photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data?.url || data?.public_url || (data?.filename ? `/api/photos/file/${data.filename}` : null);
      if (!url) {
        toast.error("Uploaded, but no URL was returned");
        return;
      }
      setForm((f) => ({ ...f, image: url }));
      toast.success("Image attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setImageUploading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, j, sub, freq] = await Promise.all([
        api.get("/admin/push/stats"),
        api.get("/admin/push/jobs?limit=200"),
        api.get("/admin/push/subscribers"),
        api.get("/admin/push/frequency").catch(() => ({ data: { random_count: 24 } })),
      ]);
      setStats(s.data);
      setJobs(j.data || []);
      setSubs(sub.data?.subscribers || []);
      setFrequency(Number(freq.data?.random_count ?? 24));
    } catch (e) {
      toast.error("Could not load push data");
      console.warn(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveFrequency = useCallback(async (n) => {
    setFreqBusy(true);
    try {
      const { data } = await api.post("/admin/push/frequency", { random_count: n });
      setFrequency(Number(data?.random_count ?? n));
      toast.success(`Frequency updated · Andreas re-seeded ${data?.reseeded?.created ?? 0} new pings`);
      // Refresh jobs so the new schedule is visible immediately
      const j = await api.get("/admin/push/jobs?limit=200");
      setJobs(j.data || []);
    } catch (_e) {
      toast.error("Couldn't save frequency");
    } finally {
      setFreqBusy(false);
    }
  }, []);

  const reloadSubs = useCallback(async () => {
    setSubsLoading(true);
    try {
      const { data } = await api.get("/admin/push/subscribers");
      setSubs(data?.subscribers || []);
    } catch (e) {
      toast.error("Could not refresh subscribers");
    } finally {
      setSubsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (sendNow = false) => {
    if (!form.title.en?.trim() || !form.body.en?.trim()) {
      toast.error("English title and body are required");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        kind: form.kind,
        audience: form.audience,
        url: form.url || "/",
        title: form.title,
        body: form.body,
        image: form.image ? form.image.trim() : null,
        schedule_at: sendNow ? new Date().toISOString() : (form.schedule_at ? new Date(form.schedule_at).toISOString() : null),
      };
      const { data } = await api.post("/admin/push/jobs", payload);
      if (sendNow && data?.id) {
        await api.post(`/admin/push/jobs/${data.id}/send-now`);
        toast.success("Pushed to subscribers");
      } else {
        toast.success("Scheduled");
      }
      setForm(emptyForm());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create job");
    } finally {
      setCreating(false);
    }
  };

  const sendNow = async (id) => {
    try {
      const { data } = await api.post(`/admin/push/jobs/${id}/send-now`);
      const delivered = data.delivered || 0;
      const failed = data.failed || 0;
      if (delivered > 0) {
        toast.success(`Delivered ${delivered}\u00a0\u00b7 failed ${failed}`);
      } else {
        toast.error(`Delivered ${delivered}\u00a0\u00b7 failed ${failed}. Check "Push self-test" for details.`, { duration: 8000 });
      }
      if ((data.errors || []).length) {
        toast.error((data.errors || []).slice(0, 2).join("\n"), { duration: 12000 });
      }
      await load();
    } catch (e) {
      toast.error("Send failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this scheduled push?")) return;
    try {
      await api.delete(`/admin/push/jobs/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/push/seed-defaults");
      toast.success(`Seeded (${data.created || 0} new)`);
      await load();
    } catch (e) {
      toast.error("Seeding failed");
    } finally {
      setSeeding(false);
    }
  };

  const draftWithAndreas = async () => {
    if (drafting) return;
    setDrafting(true);
    try {
      const { data } = await api.post("/admin/push/draft", {
        topic: draftTopic,
        kind: form.kind || "custom",
        tone: "playful",
      });
      const t = data?.title || {};
      const b = data?.body || {};
      setForm((f) => ({
        ...f,
        title: {
          en: t.en || f.title.en,
          fr: t.fr || f.title.fr,
          el: t.el || f.title.el,
          pl: t.pl || f.title.pl,
        },
        body: {
          en: b.en || f.body.en,
          fr: b.fr || f.body.fr,
          el: b.el || f.body.el,
          pl: b.pl || f.body.pl,
        },
      }));
      toast.success("Andreas drafted it, koumbare");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Andreas is taking a frappé break — try again.");
    } finally {
      setDrafting(false);
    }
  };

  const setTitle = (lang, v) => setForm((f) => ({ ...f, title: { ...f.title, [lang]: v } }));
  const setBody = (lang, v) => setForm((f) => ({ ...f, body: { ...f.body, [lang]: v } }));

  const pending = useMemo(() => jobs.filter((j) => j.status === "pending"), [jobs]);
  const sentList = useMemo(() => jobs.filter((j) => j.status === "sent"), [jobs]);

  return (
    <div className="space-y-8" data-testid="admin-push-root">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="border border-border rounded-lg p-4" data-testid="push-stat-total">
          <p className="editorial-kicker text-foreground/50">Subscribers</p>
          <p className="font-serif-display text-3xl mt-1">{stats?.subscribers?.total ?? "\u2014"}</p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <p className="editorial-kicker text-foreground/50">Pending</p>
          <p className="font-serif-display text-3xl mt-1">{stats?.jobs?.pending ?? "\u2014"}</p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <p className="editorial-kicker text-foreground/50">Sent</p>
          <p className="font-serif-display text-3xl mt-1">{stats?.jobs?.sent ?? "\u2014"}</p>
        </div>
        <div className="border border-border rounded-lg p-4">
          <p className="editorial-kicker text-foreground/50">By language</p>
          <p className="text-xs text-foreground/70 mt-1 space-x-2">
            {LANGS.map((l) => (
              <span key={l.code} className="inline-flex items-center gap-1">
                <span className="uppercase">{l.code}</span>
                <span className="text-foreground">{stats?.subscribers?.by_lang?.[l.code] ?? 0}</span>
              </span>
            ))}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={seed} disabled={seeding} data-testid="push-seed">
          {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
          Seed default Andreas jobs
        </Button>
        <Button
          variant="outline"
          onClick={async () => {
            try {
              const { data } = await api.post("/admin/push/self-test");
              const results = data.results || [];
              const delivered = results.filter((r) => r.ok).length;
              const removed = results.filter((r) => r.removed).length;
              const otherFail = results.filter((r) => !r.ok && !r.removed).length;
              const total = data.total ?? results.length;

              if (delivered > 0) {
                toast.success(`Self-test: ${delivered}/${total} delivered\u00a0\u00b7 pipeline is healthy`);
              }
              if (removed > 0) {
                toast.warning(
                  `${removed} stale subscription${removed === 1 ? "" : "s"} auto-removed (410 Gone). Devices likely uninstalled the PWA, revoked permission, or cleared site data. Ask those guests to re-enable notifications.`,
                  { duration: 14000 }
                );
              }
              if (otherFail > 0) {
                const errs = results
                  .filter((r) => !r.ok && !r.removed)
                  .slice(0, 3)
                  .map((r) => `\u2022 ${(r.error || "unknown").slice(0, 140)}`)
                  .join("\n");
                toast.error(`${otherFail} delivery failure${otherFail === 1 ? "" : "s"}:\n${errs}`, { duration: 14000 });
              }
              if (total === 0) {
                toast.info("No subscribers yet. Ask a guest to Allow notifications.");
              }
              await load();
              await reloadSubs();
            } catch (e) {
              toast.error("Self-test failed");
            }
          }}
          data-testid="push-self-test"
        >
          <Activity className="w-4 h-4 mr-2" />
          Push self-test
        </Button>
        <p className="text-xs text-foreground/50">Idempotent — safe to run any time.</p>
      </div>

      {/* Andreas frequency dial */}
      <section
        className="border border-border rounded-lg p-5 sm:p-6 bg-background/60"
        data-testid="push-frequency-card"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-10 h-10 shrink-0 rounded-full bg-accent/15 text-accent inline-flex items-center justify-center">
            <Gauge className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-serif-display text-xl">Andreas frequency</h3>
            <p className="text-sm text-foreground/65 mt-1">
              How chatty should Andreas be between now and the wedding? Milestone pushes always send —
              this only controls the random pings.
            </p>

            <div className="mt-4 flex flex-wrap gap-2" data-testid="push-frequency-presets">
              {[
                { key: "off",     value: 0,  label: "Off",     hint: "Milestones only" },
                { key: "chill",   value: 8,  label: "Chill",   hint: "~1 per week" },
                { key: "normal",  value: 24, label: "Normal",  hint: "~3 per week" },
                { key: "generous",value: 50, label: "Generous",hint: "~1 per day" },
              ].map((p) => {
                const active = frequency === p.value;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => saveFrequency(p.value)}
                    disabled={freqBusy}
                    data-testid={`push-frequency-${p.key}`}
                    className={
                      "flex flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors disabled:opacity-60 " +
                      (active
                        ? "border-accent bg-accent/10 text-accent"
                        : "border-border hover:border-accent/40 text-foreground/80")
                    }
                  >
                    <span className="text-[11px] tracking-[0.16em] uppercase font-medium">{p.label}</span>
                    <span className="text-[10px] text-foreground/50 mt-0.5">{p.hint}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex items-center gap-3">
              <label className="text-[11px] tracking-[0.14em] uppercase text-foreground/60 shrink-0">
                Custom
              </label>
              <input
                type="range"
                min={0}
                max={60}
                step={1}
                value={frequency}
                onChange={(e) => setFrequency(Number(e.target.value))}
                onMouseUp={(e) => saveFrequency(Number(e.target.value))}
                onTouchEnd={(e) => saveFrequency(Number(e.target.value))}
                disabled={freqBusy}
                data-testid="push-frequency-slider"
                className="flex-1 accent-accent"
              />
              <span className="tabular-nums font-medium min-w-[3ch] text-right">{frequency}</span>
              <span className="text-[11px] text-foreground/50">pings</span>
              {freqBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-accent" />}
            </div>
          </div>
        </div>
      </section>

      {/* Compose */}
      <section className="border border-border rounded-lg p-5 sm:p-6" data-testid="push-compose">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-4 h-4 text-accent" />
          <h3 className="font-serif-display text-xl">Compose Andreas push</h3>
        </div>

        {/* One-click Andreas drafter */}
        <div className="mb-5 rounded-lg border border-dashed border-accent/40 bg-accent/[0.06] p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 shrink-0 rounded-full bg-accent/20 text-accent flex items-center justify-center">
              <Wand2 className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="editorial-kicker text-foreground/60">Draft with Andreas</p>
              <p className="text-[13px] text-foreground/70 mt-0.5">
                Type an optional hint (or leave it blank for a random Cypriot ping) — Andreas fills all four languages in his voice.
              </p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <Input
                  value={draftTopic}
                  onChange={(e) => setDraftTopic(e.target.value)}
                  placeholder="e.g. remind everyone to book Larnaca airport"
                  data-testid="push-draft-topic"
                />
                <Button
                  type="button"
                  onClick={draftWithAndreas}
                  disabled={drafting}
                  className="sm:w-auto"
                  data-testid="push-draft-btn"
                >
                  {drafting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  {drafting ? "Andreas is typing…" : "Draft with Andreas"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="editorial-kicker text-foreground/60">Audience</Label>
            <Select value={form.audience} onValueChange={(v) => setForm((f) => ({ ...f, audience: v }))}>
              <SelectTrigger data-testid="push-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                <SelectItem value="lang:en">English speakers</SelectItem>
                <SelectItem value="lang:fr">French speakers</SelectItem>
                <SelectItem value="lang:el">Greek speakers</SelectItem>
                <SelectItem value="lang:pl">Polish speakers</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="editorial-kicker text-foreground/60">Deep link URL</Label>
            <Input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="/en/rsvp" data-testid="push-url" />
          </div>

          <div>
            <Label className="editorial-kicker text-foreground/60">Schedule at (local time)</Label>
            <Input type="datetime-local" value={form.schedule_at} onChange={(e) => setForm((f) => ({ ...f, schedule_at: e.target.value }))} data-testid="push-schedule" />
            <p className="text-[11px] text-foreground/50 mt-1">Leave empty to send immediately.</p>
          </div>
        </div>

        {/* Rich image (optional) — shows as an expanded card on Android */}
        <div className="mt-5">
          <Label className="editorial-kicker text-foreground/60 flex items-center gap-2">
            <ImageIcon className="w-3.5 h-3.5 text-accent" />
            Image (optional)
          </Label>
          <div className="flex flex-col sm:flex-row gap-3 mt-2">
            <div className="flex-1 space-y-2">
              <Input
                type="url"
                value={form.image}
                onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))}
                placeholder="https://… or /api/photos/file/<name>"
                data-testid="push-image-url"
              />
              <div className="flex items-center gap-2">
                <label
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-dashed border-border hover:border-accent hover:bg-accent/5 text-[11px] tracking-[0.14em] uppercase text-foreground/70 cursor-pointer"
                  data-testid="push-image-upload"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {imageUploading ? "Uploading…" : "Upload photo"}
                  <input
                    type="file"
                    accept="image/*"
                    disabled={imageUploading}
                    onChange={onImageUpload}
                    className="hidden"
                  />
                </label>
                {form.image && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, image: "" }))}
                    className="inline-flex items-center gap-1 h-8 px-2 text-[11px] tracking-[0.14em] uppercase text-foreground/50 hover:text-destructive"
                    data-testid="push-image-clear"
                  >
                    <X className="w-3.5 h-3.5" />
                    Remove
                  </button>
                )}
              </div>
              <p className="text-[11px] text-foreground/50">
                Best: 1200×628, &lt; 300 KB. Shows as an expanded card on Android.
                Ignored by iOS &amp; desktop Chrome.
              </p>
            </div>

            {/* Live preview */}
            {form.image && (
              <div className="w-full sm:w-56 rounded-lg border border-border overflow-hidden shrink-0">
                <img
                  src={form.image}
                  alt="preview"
                  onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                  className="w-full h-32 object-cover"
                  data-testid="push-image-preview"
                />
                <div className="p-2 bg-background/70">
                  <p className="text-[11px] font-medium text-foreground truncate">
                    {form.title.en || "Push preview"}
                  </p>
                  <p className="text-[10px] text-foreground/60 truncate">
                    {form.body.en || "This is how it will look"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 space-y-4">
          {LANGS.map((l) => (
            <div key={l.code} className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-start">
              <div className="sm:col-span-1">
                <p className="editorial-kicker text-foreground/60 mt-2">{l.label}</p>
                {l.code === "en" && <p className="text-[10px] text-destructive/70">required</p>}
              </div>
              <div className="sm:col-span-4 space-y-2">
                <Input
                  value={form.title[l.code]}
                  onChange={(e) => setTitle(l.code, e.target.value)}
                  placeholder={`Title (${l.code})`}
                  data-testid={`push-title-${l.code}`}
                />
                <Textarea
                  value={form.body[l.code]}
                  onChange={(e) => setBody(l.code, e.target.value)}
                  placeholder={`Body (${l.code}) \u2014 Andreas voice: siga siga, reee, koumbare\u2026`}
                  rows={2}
                  data-testid={`push-body-${l.code}`}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={() => create(false)} disabled={creating} data-testid="push-schedule-btn">
            <Clock className="w-4 h-4 mr-2" /> Schedule
          </Button>
          <Button onClick={() => create(true)} disabled={creating} data-testid="push-sendnow-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Send now
          </Button>
        </div>
      </section>

      {/* Subscribers */}
      <section data-testid="push-subscribers-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif-display text-xl">Subscribers ({subs.length})</h3>
          <Button size="sm" variant="outline" onClick={reloadSubs} disabled={subsLoading} data-testid="push-subs-refresh">
            <RefreshCw className={"w-3.5 h-3.5 mr-2 " + (subsLoading ? "animate-spin" : "")} />
            Refresh
          </Button>
        </div>
        {subs.length === 0 ? (
          <p className="text-sm text-foreground/50">
            No devices are subscribed yet. Guests need to tap Allow in the notifications modal or the mobile drawer’s panel.
          </p>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <Table data-testid="push-subs-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Device</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Lang</TableHead>
                  <TableHead>Where</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead>Last error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subs.map((s) => {
                  const ua = s.user_agent || "";
                  const isMobile = /iPhone|iPad|Android|Mobile/i.test(ua);
                  const shortUa = ua
                    .replace(/Mozilla\/[\d.]+\s*/g, "")
                    .replace(/\(KHTML,[^)]*\)/g, "")
                    .replace(/AppleWebKit\/[\d.]+\s*/g, "")
                    .slice(0, 70);
                  // Zone → colour + friendly label
                  const zoneMap = {
                    at_venue: { color: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30", label: "At venue" },
                    near_venue: { color: "bg-accent/10 text-accent border-accent/30", label: "Near venue" },
                    in_cyprus: { color: "bg-amber-500/10 text-amber-700 border-amber-500/30", label: "In Cyprus" },
                    far: { color: "bg-foreground/5 text-foreground/60 border-border", label: "Far" },
                  };
                  const zoneInfo = zoneMap[s.zone];
                  return (
                    <TableRow key={s.id || s.endpoint_short} data-testid={`push-sub-row-${(s.id || "").slice(0,8)}`}>
                      <TableCell className="max-w-[280px]">
                        <div className="flex items-start gap-2">
                          <Smartphone className={"w-3.5 h-3.5 mt-1 shrink-0 " + (isMobile ? "text-accent" : "text-foreground/40")} />
                          <div className="min-w-0">
                            {s.guest_name && (
                              <p className="text-xs text-foreground/80 font-medium truncate">{s.guest_name}</p>
                            )}
                            <p className="text-xs text-foreground/70 truncate" title={ua}>{shortUa || "—"}</p>
                            <p className="text-[10px] text-foreground/40 truncate" title={s.endpoint_short}>{s.endpoint_short}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="whitespace-nowrap">{s.service || "Other"}</Badge></TableCell>
                      <TableCell className="uppercase text-xs text-foreground/60">{s.lang}</TableCell>
                      <TableCell className="max-w-[160px]">
                        {zoneInfo ? (
                          <div className="flex flex-col gap-0.5">
                            <span className={"inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border w-fit " + zoneInfo.color}>
                              <MapPin className="w-2.5 h-2.5" />
                              {zoneInfo.label}
                            </span>
                            {typeof s.distance_venue_km === "number" && s.distance_venue_km > 0 && (
                              <span className="text-[10px] text-foreground/50" title="Distance to venue">
                                {s.distance_venue_km < 1
                                  ? `${Math.round(s.distance_venue_km * 1000)} m to venue`
                                  : `${s.distance_venue_km.toFixed(1)} km to venue`}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[10px] text-foreground/40 italic">no location</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-foreground/60 whitespace-nowrap">{fmt(s.last_seen_at)}</TableCell>
                      <TableCell className="max-w-[260px]">
                        {s.last_error ? (
                          <div className="flex items-start gap-1.5">
                            <AlertCircle className="w-3.5 h-3.5 mt-0.5 text-destructive shrink-0" />
                            <span className="text-[11px] text-destructive truncate" title={s.last_error}>{s.last_error}</span>
                          </div>
                        ) : (
                          <span className="text-[11px] text-foreground/40">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* Pending */}
      <section>
        <h3 className="font-serif-display text-xl mb-3">Pending ({pending.length})</h3>
        {loading ? (
          <p className="text-sm text-foreground/50">Loading…</p>
        ) : pending.length === 0 ? (
          <p className="text-sm text-foreground/50">No pending pushes. Seed defaults or compose one above.</p>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <Table data-testid="push-pending-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Scheduled</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Audience</TableHead>
                  <TableHead>Preview (EN)</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((j) => (
                  <TableRow key={j.id} data-testid={`push-pending-row-${j.id}`}>
                    <TableCell className="whitespace-nowrap">{fmt(j.schedule_at)}</TableCell>
                    <TableCell><Badge variant="secondary" className={kindColor[j.kind] || ""}>{j.kind}</Badge></TableCell>
                    <TableCell className="text-xs">{j.audience}</TableCell>
                    <TableCell className="max-w-[380px]">
                      <p className="text-sm font-medium truncate">{j.title?.en}</p>
                      <p className="text-xs text-foreground/60 truncate">{j.body?.en}</p>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => sendNow(j.id)} data-testid={`push-sendnow-${j.id}`}>
                        <Send className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(j.id)} data-testid={`push-remove-${j.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* Sent */}
      <section>
        <h3 className="font-serif-display text-xl mb-3">Sent ({sentList.length})</h3>
        {sentList.length === 0 ? (
          <p className="text-sm text-foreground/50">Nothing sent yet.</p>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <Table data-testid="push-sent-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Sent at</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Delivered / Failed</TableHead>
                  <TableHead>Preview (EN)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sentList.slice(0, 40).map((j) => (
                  <TableRow key={j.id}>
                    <TableCell className="whitespace-nowrap">{fmt(j.sent_at || j.schedule_at)}</TableCell>
                    <TableCell><Badge variant="secondary" className={kindColor[j.kind] || ""}>{j.kind}</Badge></TableCell>
                    <TableCell className="text-xs"><Check className="inline w-3 h-3 mr-1 text-green-600" />{j.delivered || 0} / <span className="text-destructive">{j.failed || 0}</span></TableCell>
                    <TableCell className="max-w-[380px]">
                      <p className="text-sm truncate">{j.title?.en}</p>
                      <p className="text-xs text-foreground/60 truncate">{j.body?.en}</p>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminPush;
